import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, PlayCircle, BookOpen, Mail } from "lucide-react";
import { fetchProjects, fetchProfile } from "@/lib/api";
import { PhotoGallery } from "@/components/PhotoGallery";
import { usePageMeta } from "@/components/Seo";

const SEO_TITLES = {
  director: "Direction — Short Films & Commercials",
  producer: "Production — Independent & Qissa Studios",
  writer: "Writing — Poetry · Gubaar",
  photographer: "Photography",
};

const ROLE_LABEL = {
  director: "Direction",
  producer: "Production",
  writer: "Writing",
  photographer: "Photography",
};

const ROLE_SLUG = {
  director: "director",
  producer: "production",
  writer: "writing",
  photographer: "photography",
};

const CONFIG = {
  director: {
    overline: "Direction · Short films & commercials",
    headline_a: "Confident",
    headline_b: "in every frame.",
    manifesto:
      "I direct across formats — from minute-long brand films to award-travelling short films. Below, the work is split between the two: commercials first, then short films. I learn from both. The discipline of a fifteen-second spot teaches patience to the longer form; the freedom of the short keeps the spot honest.",
    sub: "Selected directorial work. Click any project for credits and the embedded film.",
    tabs: [
      { id: "all", label: "All" },
      { id: "short_films", label: "Short Films" },
      { id: "commercials", label: "Commercials" },
    ],
  },
  producer: {
    overline: "Production · Independent & Qissa Studios",
    headline_a: "The set, the sweat,",
    headline_b: "and the storytelling.",
    manifesto:
      "I produce work in two lanes. The first is independent — short films, features, and series I choose to put my own money and time behind. The second is Qissa Studios — the company I founded, where we've produced 100+ commercials and branded films for Netflix, Google, RedBull, SanDisk, Apollo, IndiaFirst Life and many more.",
    sub: "Independent productions first, then Qissa Studios work. Switch tabs to filter.",
    tabs: [
      { id: "all", label: "All" },
      { id: "independent", label: "Independent" },
      { id: "qissa", label: "Qissa Studios" },
    ],
  },
  writer: {
    overline: "Writing · Poetry",
    headline_a: "The page",
    headline_b: "before the lens.",
    manifesto:
      "Before the camera, the page. Beyond filmmaking, I write poetry — in Hindi and Urdu. My debut collection, ग़ुबार · Gubaar, was written over 21 days as a search for inspiration in the world inside and around me.",
    sub: null,
    tabs: null,
  },
  photographer: {
    overline: "Photography · The first craft",
    headline_a: "It began",
    headline_b: "behind a stills camera.",
    manifesto:
      "My filmmaking career began in photojournalism and television — a craft of waiting, framing, and trusting the available light. Below is a curated selection of frames from my work; the newest images land on Instagram first.",
    sub: null,
    tabs: null,
  },
};

export default function DisciplinePage({ role }) {
  const [projects, setProjects] = useState([]);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const cfg = CONFIG[role];

  usePageMeta({ title: SEO_TITLES[role], description: cfg.manifesto });

  useEffect(() => {
    setActiveTab("all");
    fetchProjects({ role }).then(setProjects).catch(() => {});
    if (role === "producer") fetchProfile().then(setProfile).catch(() => {});
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [role]);

  const filtered = useMemo(() => {
    let list = projects;
    if (role === "director") {
      const rank = (p) => (p.sub_category === "commercials" ? 0 : 1);
      list = [...projects].sort((a, b) => rank(a) - rank(b));
    }
    if (!cfg.tabs || activeTab === "all") return list;
    return list.filter((p) => p.sub_category === activeTab);
  }, [projects, activeTab, cfg.tabs, role]);

  // Special layouts
  const isWriting = role === "writer";
  const isPhotography = role === "photographer";

  return (
    <main data-testid={`discipline-${role}-page`} className="bg-zinc-950 text-zinc-50 pt-32 md:pt-40">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <p data-testid={`discipline-${role}-overline`} className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
          {cfg.overline} · {projects.length} {projects.length === 1 ? "work" : "works"}
        </p>
        <h1
          data-testid={`discipline-${role}-headline`}
          className="font-serif text-5xl md:text-7xl lg:text-9xl leading-[0.95] tracking-tight max-w-5xl"
        >
          {cfg.headline_a}
          <br />
          <span className="italic text-zinc-400">{cfg.headline_b}</span>
        </h1>

        <div className="mt-16 md:mt-20 grid grid-cols-12 gap-x-12 gap-y-10">
          <div className="col-span-12 md:col-span-7">
            <p className="text-zinc-300 text-lg md:text-xl leading-relaxed">{cfg.manifesto}</p>
          </div>
          {cfg.sub && (
            <div className="col-span-12 md:col-span-4 md:col-start-9 border-l border-zinc-900 pl-8 md:pl-12">
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                About these works
              </p>
              <p className="text-zinc-400 text-sm leading-relaxed">{cfg.sub}</p>
            </div>
          )}
        </div>
      </section>

      {/* Production House callout — only for producer */}
      {role === "producer" && profile?.production_house && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32">
          <a
            href={profile.production_house.url}
            target="_blank"
            rel="noreferrer"
            data-testid="production-house-link"
            className="group block border border-zinc-900 hover:border-zinc-700 bg-zinc-900/40 hover:bg-zinc-900 transition-colors p-8 md:p-12"
          >
            <div className="grid grid-cols-12 gap-x-8 gap-y-6 items-end">
              <div className="col-span-12 md:col-span-8">
                <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                  The production company · Est. {profile.production_house.founded}
                </p>
                <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-zinc-50 leading-tight">
                  {profile.production_house.name}
                  <span className="inline-block ml-4 align-baseline">
                    <ArrowUpRight className="h-8 w-8 md:h-12 md:w-12 text-zinc-500 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" strokeWidth={1} />
                  </span>
                </h2>
                <p className="text-zinc-400 text-base md:text-lg mt-6 max-w-2xl">
                  {profile.production_house.tagline}
                </p>
              </div>
              <div className="col-span-12 md:col-span-3 md:col-start-10">
                <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-300 group-hover:text-white link-underline pb-1">
                  Visit qissastudios.in
                </span>
              </div>
            </div>
          </a>
        </section>
      )}

      {/* WRITING — Poetry book feature */}
      {isWriting && projects[0] && <PoetryFeature project={projects[0]} />}

      {/* PHOTOGRAPHY — gallery + archive note */}
      {isPhotography && (
        <>
          <PhotoGallery />
          <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-zinc-900">
          <div className="grid grid-cols-12 gap-x-10 gap-y-10 items-end">
            <div className="col-span-12 md:col-span-7 aspect-[16/10] overflow-hidden">
              <img
                src={projects[0]?.cover || "https://images.unsplash.com/photo-1621993646147-8cde7b9cc031"}
                alt="Photojournalism archive"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9">
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
                Archive · 2010 — 2018
              </p>
              <h3 className="font-serif text-3xl md:text-4xl text-zinc-100 leading-tight mb-6">
                A private collection,
                <br />
                <span className="italic text-zinc-400">released on request.</span>
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed mb-8">
                Selected work from a decade in photojournalism and television. Available
                as a private viewing for serious enquiries — editorial, gallery, or
                personal interest.
              </p>
              <a
                href="mailto:Sharma.chinmaya12@gmail.com?subject=Photography%20archive%20enquiry"
                data-testid="photography-request-link"
                className="group inline-flex items-center gap-3 bg-white text-zinc-950 px-6 py-3 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors"
              >
                <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                Request a viewing
              </a>
            </div>
          </div>
          </section>
        </>
      )}

      {/* Director / Producer — tabs + grid */}
      {!isWriting && !isPhotography && (
        <>
          {cfg.tabs && (
            <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mt-20 md:mt-24">
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-4 border-b border-zinc-900 pb-6">
                {cfg.tabs.map((t) => {
                  const count =
                    t.id === "all"
                      ? projects.length
                      : projects.filter((p) => p.sub_category === t.id).length;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      data-testid={`discipline-${role}-tab-${t.id}`}
                      className={`group text-[11px] uppercase tracking-[0.3em] inline-flex items-baseline gap-2 transition-colors ${
                        activeTab === t.id
                          ? "text-white"
                          : "text-zinc-500 hover:text-zinc-200"
                      }`}
                    >
                      <span className="link-underline pb-0.5">{t.label}</span>
                      <sup className="text-[9px] text-zinc-600 tabular-nums">
                        {String(count).padStart(2, "0")}
                      </sup>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24">
            {filtered.length === 0 ? (
              <p data-testid={`discipline-${role}-empty`} className="text-center text-zinc-500 py-24 font-serif italic text-2xl">
                Nothing in this category yet — try another tab.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filtered.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/work/${p.slug}`}
                    data-testid={`discipline-${role}-project-${p.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-video overflow-hidden bg-zinc-900">
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-zinc-950/0 to-zinc-950/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {p.video_embed && (
                        <div className="absolute top-4 right-4 text-zinc-100 opacity-80 group-hover:opacity-100">
                          <PlayCircle className="h-6 w-6" strokeWidth={1} />
                        </div>
                      )}
                      {p.status === "in_development" && (
                        <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.25em] text-zinc-200 bg-zinc-950/70 backdrop-blur px-2.5 py-1">
                          In development
                        </span>
                      )}
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                          {p.category}
                        </p>
                        <p className="text-[10px] text-zinc-500 tabular-nums">{p.year}</p>
                      </div>
                      <h3 className="font-serif text-2xl md:text-[1.65rem] text-zinc-100 group-hover:text-white transition-colors leading-tight">
                        {p.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Cross-discipline links */}
      <section className="border-t border-zinc-900 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-16">
        <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
          Other disciplines
        </p>
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          {Object.keys(CONFIG)
            .filter((r) => r !== role)
            .map((r) => (
              <Link
                key={r}
                to={`/${ROLE_SLUG[r]}`}
                data-testid={`discipline-cross-${r}`}
                className="group font-serif italic text-3xl md:text-5xl text-zinc-400 hover:text-white transition-colors"
              >
                {ROLE_LABEL[r]}
                <ArrowUpRight className="inline-block h-5 w-5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1} />
              </Link>
            ))}
        </div>
      </section>
    </main>
  );
}

function PoetryFeature({ project }) {
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
      <div className="grid grid-cols-12 gap-x-12 gap-y-12 items-start">
        <div className="col-span-12 md:col-span-5">
          <div className="relative aspect-[2/3] overflow-hidden bg-zinc-900">
            <img
              src={project.cover}
              alt={project.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/60 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-300 mb-3">
                {project.category} · {project.year}
              </p>
              <p className="font-serif text-3xl md:text-4xl text-zinc-50 leading-tight">
                {project.title}
              </p>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 md:col-start-7">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6 inline-flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" strokeWidth={1.5} />
            Debut collection
          </p>
          <h2 data-testid="poetry-title" className="font-serif text-4xl md:text-6xl leading-[1.05] text-zinc-50">
            ग़ुबार
            <br />
            <span className="italic text-zinc-400">Gubaar</span>
          </h2>
          <p className="font-serif italic text-xl md:text-2xl text-zinc-300 mt-8 leading-relaxed">
            A bouquet of emotions, thoughts and imaginations.
          </p>
          <p className="text-zinc-400 text-base leading-relaxed mt-6">
            {project.synopsis}
          </p>

          <ul className="mt-10 space-y-3 border-t border-zinc-900 pt-8">
            {Object.entries(project.credits || {}).map(([k, v]) => (
              <li key={k} className="flex items-baseline gap-6">
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 w-24 shrink-0">
                  {k}
                </span>
                <span className="text-zinc-200 text-sm">{v}</span>
              </li>
            ))}
          </ul>

          {project.external_url && (
            <a
              href={project.external_url}
              target="_blank"
              rel="noreferrer"
              data-testid="poetry-buy-link"
              className="mt-10 group inline-flex items-center gap-3 bg-white text-zinc-950 px-8 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors"
            >
              {project.external_label || "Buy the book"}
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
